import {
  faCogs,
  faDownload, faExclamationTriangle,
  faFilter
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import equals from "fast-deep-equal";
import React from "react";
import { ButtonGroup, Col, Container, Dropdown, DropdownButton, Row, ToggleButton, Badge } from "react-bootstrap";
import { selectFilter, textFilter, dateFilter } from "react-bootstrap-table2-filter";
import { withTranslation } from "react-i18next";
import ConfirmationDialog from "../../../../components/dialogs/confirmationDialog/ConfirmationDialog";
import RunningDownloadDialog from "../../../../components/dialogs/runningDownloadDialog/RunningDownloadDialog";
import PaginationTable from "../../../../components/paginationTable/PaginationTable";
import APIErrorHelper from "../../../../helpers/APIErrorHelper";
import InstanceStatus from "../../../../helpers/InstanceStatus";
import CamplaCoreService from "../../../../services/CamplaCoreService";
import StorageService from "../../../../services/StorageService";
import ManagePaperNotes from "./dialogs/managePaperNotes/ManagePaperNotes";
import ExamInstanceActionCell from "./examInstanceActionCell/ExamInstanceActionCell";
import ExaminationInstanceStatusCell from "./examInstanceStatusCell/ExaminationInstanceStatusCell";
import ExaminationInstanceSubmissionCell from "./examInstanceSubmissionCell/ExaminationInstanceSubmissionCell";
import ExaminationInstanceIndicatorCell from "./examInstanceIndicatorCell/ExaminationInstanceIndicatorCell";
import StudentExaminationFileManager from "./dialogs/studentExaminationFileManager/StudentExaminationFileManager";
import ManageReplacementDevices from "./dialogs/manageReplacementDevices/ManageReplacementDevices";
import ShowSecretsDialog from "../../../../components/dialogs/showSecretsDialog/ShowSecretsDialog";
import "./InstancesTab.css";

class InstancesTab extends React.Component {
  constructor(props) {
    super(props);
    this.tableId = "instancesTabTable";
    this.state = {
      instances: [],
      instancePaperNotes: new Set(),
      instanceIndexMap: new Map(),
      selectedInstance: null,
      instanceStorageIndexMap: new Map(),
      showPaperNotesModal: false,
      showReplacementDeviceModal: false,
      studentExaminationFileManagerModal: false,
      showConfirmationDialog: false,
      secretsDialogShow: false,
      secretDialogTitle: "",
      secretDialogLabel: "",
      confirmationDialogTitle: "",
      confirmationDialogBody: "",
      confirmationDialogRightButtonVariant: "primary",
      confirmationDialogLeftButtonVariant: "secondary",
      confirmationDialogRightButtonText: "",
      runningDownloadDialogShow: false,
      runningDownloadDialogTitle: "",
      runningDownloadDialogProgress: 0,
      currentSecret: "",
      dialogApiError: "",
      paperNotesUpdate: Date.now(),
      apiError: "",
      showFilters: false,
      displayFilter: "none"
    };

    this.fetchData = this.fetchData.bind(this);
    this.eventListener = this.eventListener.bind(this);
    this.registerEventListener = this.registerEventListener.bind(this);
    this.setActionConfirmationState = this.setActionConfirmationState.bind(
      this
    );
    this.onStart = this.onStart.bind(this);
    this.onStop = this.onStop.bind(this);
    this.onDownloadSubmission = this.onDownloadSubmission.bind(this);
    this.onDownloadBackup = this.onDownloadBackup.bind(this);
    this.onExamFileDownloadReset = this.onExamFileDownloadReset.bind(this);
    this.onPaperNotesUpdate = this.onPaperNotesUpdate.bind(this);
    this.onDownloadPaperNotes = this.onDownloadPaperNotes.bind(this);
    this.onHasNotes = this.onHasNotes.bind(this);
    this.onStudentExamFileUpload = this.onStudentExamFileUpload.bind(this);
    this.onDeviceAssignment = this.onDeviceAssignment.bind(this);
    this.onGenerateOneTimeToken = this.onGenerateOneTimeToken.bind(this);
    this.onGenerateOneTimeStudentLogin = this.onGenerateOneTimeStudentLogin.bind(this);
    this.onDeviceReset = this.onDeviceReset.bind(this);
    this.onSEBSessionQuit = this.onSEBSessionQuit.bind(this);
  }

  async componentDidMount() {
    // Load data from cache
    const instances = StorageService.getReferenceType("instances");
    const instanceIndexMap = StorageService.getReferenceType(
      "instanceIndexMap",
      "map"
    );
    if (instances) {
      this.setState({
        instances,
        instanceIndexMap
      });
    }
    await this.fetchData();
    this.registerEventListener();
  }

  async componentDidUpdate(prevProps) {
    if (!equals(this.props.examination, prevProps.examination)) {
      await this.fetchData();
    }
  }

  componentWillUnmount() {
    CamplaCoreService.removeEventListener("InstancesTab");
  }

  async fetchData() {
    if (!this.props.examination) return;
    try {
      const instances = await CamplaCoreService.getExaminationInstancesWithStorage(
        this.props.examination._id
      );
      const instancePaperNotes = new Set(
        await CamplaCoreService.getAllPaperNotes(this.props.examination._id)
      );
      const instanceIndexMap = new Map();
      const instanceStorageIndexMap = new Map();
      instances.forEach((v, idx) => {
        if (typeof instances[idx]["storage"][0] !== "undefined") {
          const date = new Date(
            Date.parse(instances[idx]["storage"][0]["lastFileChange"])
          );
          instances[idx]["lastFileChange"] = new Date(
              Date.parse(instances[idx]["storage"][0]["lastFileChange"])
          );
          instances[idx]["lastSubmission"] = {
            date: new Date(
              Date.parse(instances[idx]["storage"][0]["lastFileChange"])
            ),
            examinationStatus: this.props.examination.status,
            isInit:
              date.getTime() <
              new Date(Date.parse(this.props.examination.updated_at)).getTime(),
            firstLoad: true
          };
        } else {
          instances[idx]["lastFileChange"] = new Date();
          instances[idx]["lastSubmission"] = {
            date: new Date(),
            examinationStatus: this.props.examination.status,
            isInit: true,
            firstLoad: true
          };
        }
        instanceStorageIndexMap.set(v.storageId, idx);
        instanceIndexMap.set(v._id, idx);
      });
      StorageService.set("instances", instances);
      StorageService.set("instanceIndexMap", instanceIndexMap, "map");
      StorageService.set(
        "instanceStorageIndexMap",
        instanceStorageIndexMap,
        "map"
      );
      this.setState({
        instancePaperNotes,
        instances,
        instanceIndexMap,
        instanceStorageIndexMap
      });
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        )
      });
    }
  }

  eventListener(event) {
    if (event.model === "examinations" && event.action === "update") {
      const updatedInstances = [];
      this.state.instances.forEach(inst => {
        const clonedInstance = Object.assign({}, inst);
        clonedInstance["lastFileChange"] = inst.lastSubmission.date;
        clonedInstance["lastSubmission"] = {
          date: inst.lastSubmission.date,
          examinationStatus: event.object.status,
          isInit:
            inst.lastSubmission.date.getTime() <
            new Date(Date.parse(event.object.updated_at)).getTime(),
          firstLoad: inst.lastSubmission.firstLoad
        };
        updatedInstances.push(clonedInstance);
      });
      this.setState({ instances: updatedInstances });
    }
    if (
      event.model === "instances" &&
      event.action === "update" &&
      this.state.instanceIndexMap.has(event.object._id)
    ) {
      const idx = this.state.instanceIndexMap.get(event.object._id);
      const clonedInstance = Object.assign({}, this.state.instances[idx]);
      clonedInstance["hasStarted"] = event.object.hasStarted;
      clonedInstance["status"] = event.object.status;
      clonedInstance["memberDevice"] = event.object.memberDevice;
      clonedInstance["memberDeviceHealth"] = event.object.memberDeviceHealth;
      const newInstances = this.state.instances.map(
        (inst, i) => (i === idx ? clonedInstance : inst)
      );
      StorageService.set("instances", newInstances);
      this.setState({ instances: newInstances });
    }
    if (
      event.model === "instanceStorage" &&
      event.action === "update" &&
      this.state.instanceStorageIndexMap.has(event.object._id)
    ) {
      const idx = this.state.instanceStorageIndexMap.get(event.object._id);
      const clonedInstance = Object.assign({}, this.state.instances[idx]);
      clonedInstance["lastFileChange"] = new Date(Date.parse(event.object.lastFileChange));
      clonedInstance["lastSubmission"] = {
        date: new Date(Date.parse(event.object.lastFileChange)),
        examinationStatus: this.props.examination.status,
        isInit: false,
        firstLoad: false
      };
      const instances = [...this.state.instances];
      instances[idx] = clonedInstance;
      StorageService.set("instances", instances);
      this.setState({ instances });
    }
  }

  registerEventListener() {
    CamplaCoreService.addEventListener("InstancesTab", this.eventListener);
  }

  onPaperNotesUpdate(paperNotes) {
    this.setState({
      paperNotesUpdate: Date.now(),
      instancePaperNotes: new Set(paperNotes)
    });
  }


  async onDownloadPaperNotes(instance) {
    try {
      await CamplaCoreService.downloadPaperNotes(
        this.props.examination._id,
        instance._id,
        `${instance.memberEmail.split("@")[0]}-notes.zip`
      );
    } catch (err) {
      //Todo: Add correct errors according to documentation
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleExamination.InstanceTab.APIError.PreconditionFailed",
            NOT_FOUND: "SingleExamination.InstanceTab.APIError.NotFound",
            BAD_REQUEST: "SingleExamination.InstanceTab.BadRequest"
          },
          this.props.t
        )
      });
    }
  }

  async onGenerateOneTimeStudentLogin(instance){
    try {
      const {password, email} = await CamplaCoreService.generateOneTimeStudentLogin(instance._id);
      if (password){
        this.setState({
          secretDialogLabel: email,
          secretDialogTitle: this.props.t("SingleExamination.InstanceTab.ActionCell.StudentPasswordOption"),
          currentSecret: password,
          secretsDialogShow: true
        })
      }
    } catch (err) {
      //Todo: Add correct errors according to documentation
      this.setState({
        dialogApiError: APIErrorHelper.getError(
            err,
            () => this.setState({ dialogApiError: "" }),
            {},
            this.props.t
        )
      });
    }
  }

  async onGenerateOneTimeToken(instance){
    try {
      const {token} = await CamplaCoreService.generateOneTimeToken(instance._id);
      if (token){
        this.setState({
          secretDialogLabel: this.props.t("SingleExamination.InstanceTab.ActionCell.AdminTokenOption"),
          secretDialogTitle: this.props.t("SingleExamination.InstanceTab.ActionCell.AdminTokenOption"),
          currentSecret: token,
          secretsDialogShow: true
        })
      }
    } catch (err) {
      //Todo: Add correct errors according to documentation
      this.setState({
        dialogApiError: APIErrorHelper.getError(
            err,
            () => this.setState({ dialogApiError: "" }),
            {},
            this.props.t
        )
      });
    }
  }

  async onSEBSessionQuit(instance){
    await CamplaCoreService.quitSEBSession(instance._id);
  } catch (err) {
    this.setState({
      dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {},
          this.props.t
      )
    });
  }

  onStudentExamFileUpload(instance){
    this.setState({
      selectedInstance: instance,
      studentExaminationFileManagerModal: true
    })
  }

  onHasNotes(instance) {
    return this.state.instancePaperNotes.has(instance._id);
  }

  async onDeviceReset(instance){
    try {
      await CamplaCoreService.resetRAOfInstance(instance._id);
    } catch (err) {
      //Todo: Add correct errors according to documentation
      this.setState({
        dialogApiError: APIErrorHelper.getError(
            err,
            () => this.setState({ dialogApiError: "" }),
            {},
            this.props.t
        )
      });
    }
  }

  onDeviceAssignment(instance){
    this.setState({
      selectedInstance: instance,
      showReplacementDeviceModal: true
    })
  }

  async onStart(instance) {
    const { t } = this.props;

    this.setActionConfirmationState(
      t("SingleExamination.InstanceTab.ConfirmationDialog.Start.Title"),
      t("SingleExamination.InstanceTab.ConfirmationDialog.Start.Body", {
        memberEmail: instance.memberEmail
      }),
      async () => {
        try {
          await CamplaCoreService.startInstance(instance._id);
          this.setState({ showConfirmationDialog: false });
        } catch (err) {
          //Todo: Add correct errors according to documentation
          this.setState({
            dialogApiError: APIErrorHelper.getError(
              err,
              () => this.setState({ dialogApiError: "" }),
              {
                PRECONDITION_FAILED:
                  "SingleExamination.InstanceTab.APIError.PreconditionFailed",
                NOT_FOUND: "SingleExamination.InstanceTab.APIError.NotFound",
                BAD_REQUEST: "SingleExamination.InstanceTab.BadRequest"
              },
              t
            )
          });
        }
      },
      "green",
      t("SingleExamination.InstanceTab.ConfirmationDialog.Start.Confirm")
    );
  }

  async onStop(instance) {
    const { t } = this.props;

    this.setActionConfirmationState(
      t("SingleExamination.InstanceTab.ConfirmationDialog.Stop.Title"),
      t("SingleExamination.InstanceTab.ConfirmationDialog.Stop.Body", {
        memberEmail: instance.memberEmail
      }),
      async () => {
        try {
          await CamplaCoreService.stopInstance(instance._id);
          this.setState({ showConfirmationDialog: false });
        } catch (err) {
          //Todo: Add correct errors according to documentation
          this.setState({
            dialogApiError: APIErrorHelper.getError(
              err,
              () => this.setState({ dialogApiError: "" }),
              {
                PRECONDITION_FAILED:
                  "SingleExamination.InstanceTab.APIError.PreconditionFailed",
                NOT_FOUND: "SingleExamination.InstanceTab.APIError.NotFound",
                BAD_REQUEST: "SingleExamination.InstanceTab.BadRequest"
              },
              t
            )
          });
        }
      },
      "red",
      t("SingleExamination.InstanceTab.ConfirmationDialog.Stop.Confirm")
    );
  }

  async onDownloadBackup(instance) {
    try {
      const filename = `${instance.memberEmail.split("@")[0]}-backup.zip`;
      this.setState({
        runningDownloadDialogShow: true,
        runningDownloadDialogTitle: filename,
        runningDownloadDialogProgress: 0
      });
      await CamplaCoreService.downloadInstanceBackup(
        instance._id,
        filename,
        percentage =>
          this.setState({ runningDownloadDialogProgress: percentage })
      );
      this.setState({
        runningDownloadDialogShow: false,
        runningDownloadDialogTitle: "",
        runningDownloadDialogProgress: 0
      });
    } catch (err) {
      //Todo: Add correct errors according to documentation
      this.setState({
        runningDownloadDialogShow: false,
        runningDownloadDialogTitle: "",
        runningDownloadDialogProgress: 0,
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleExamination.InstanceTab.APIError.PreconditionFailed",
            NOT_FOUND: "SingleExamination.InstanceTab.APIError.NotFound",
            BAD_REQUEST: "SingleExamination.InstanceTab.BadRequest"
          },
          this.props.t
        )
      });
    }
  }

  async onDownloadSubmission(instance) {
    try {
      const filename = `${instance.memberEmail.split("@")[0]}.zip`;
      this.setState({
        runningDownloadDialogShow: true,
        runningDownloadDialogTitle: filename,
        runningDownloadDialogProgress: 0
      });
      await CamplaCoreService.downloadInstanceSubmission(
        instance._id,
        filename,
        percentage =>
          this.setState({ runningDownloadDialogProgress: percentage })
      );
      this.setState({
        runningDownloadDialogShow: false,
        runningDownloadDialogTitle: "",
        runningDownloadDialogProgress: 0
      });
    } catch (err) {
      //Todo: Add correct errors according to documentation
      this.setState({
        runningDownloadDialogShow: false,
        runningDownloadDialogTitle: "",
        runningDownloadDialogProgress: 0,
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleExamination.InstanceTab.APIError.PreconditionFailed",
            NOT_FOUND: "SingleExamination.InstanceTab.APIError.NotFound",
            BAD_REQUEST: "SingleExamination.InstanceTab.BadRequest"
          },
          this.props.t
        )
      });
    }
  }

  async onExamFileDownloadReset(instance) {
    try {
      await CamplaCoreService.resetExamFileDownload(instance._id);
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleExamination.InstanceTab.APIError.PreconditionFailed",
            NOT_FOUND: "SingleExamination.InstanceTab.APIError.NotFound",
            BAD_REQUEST: "SingleExamination.InstanceTab.BadRequest"
          },
          this.props.t
        )
      });
    }
  }

  setActionConfirmationState(title, body, submitFunction, submitButtonVariant, submitButtonText) {
    this.setState({
      showConfirmationDialog: true,
      confirmationDialogTitle: title,
      confirmationDialogBody: body,
      onConfirmationDialogSubmit: submitFunction,
      confirmationDialogRightButtonVariant: submitButtonVariant,
      confirmationDialogRightButtonText: submitButtonText,
      confirmationDialogLeftButtonVariant: "black"
    });
  }

  render() {
    const { t } = this.props;
    const actionCellData = {
      examinationStatus: this.props.examination.status,
      paperNotesUpdate: this.state.paperNotesUpdate
    };
    const currentTableId = typeof this.props["examination"] !== "undefined"
    && this.props["examination"] !== null
    && typeof this.props.examination["_id"] !== "undefined" ?
        `${this.tableId}-${this.props.examination._id}` : this.tableId;
    const columns = [
      {
        dataField: "memberDeviceHealth",
        text: "",
        headerStyle: () => ({ width: "85px" }),
        formatExtraData: actionCellData,
        formatter: (hasStarted, instance) => (
            <ExaminationInstanceIndicatorCell
                onHasNotes={this.onHasNotes}
                instance={instance}
                examination={this.props.examination}
                id={instance._id}/>
        ),
        sort: false
      },
      {
        dataField: "memberEmail",
        text: t("SingleExamination.InstanceTab.ColumnMemberEmail"),
        sort: true,
        filter: textFilter({
          placeholder: t(
            "SingleExamination.InstanceTab.FilterMemberEmailPlaceholder"
          ),
          style: { display: this.state.displayFilter }
        })
      },
      {
        dataField: "status",
        text: t("Examinations.ColumnStatus"),
        sort: true,
        filter: selectFilter({
          options: InstanceStatus.getStatus().map(s => ({
            value: s.code,
            label: t(s.translation)
          })),
          style: { display: this.state.displayFilter }
        }),
        formatter: cell => <ExaminationInstanceStatusCell code={cell} />
      },
      {
        dataField: "lastFileChange",
        text: t("SingleExamination.InstanceTab.ColumnLastSubmission"),
        sort: true,
        filter: dateFilter({
          placeholder: t(
            "SingleExamination.InstanceTab.FilterLastSubmissionPlaceholder"
          ),
          style: { display: this.state.displayFilter }
        }),
        formatter: (lastFileChange, instance) => (
          <ExaminationInstanceSubmissionCell
              submission={typeof instance["lastSubmission"] === "undefined" ? null : instance.lastSubmission}
              instance={instance}
          />
        )
      },
      {
        dataField: "actions",
        text: "",
        headerFormatter: () => (
          <ButtonGroup
            toggle
            style={{ float: "right", marginRight: "-12px" }}
          >
            <ToggleButton
              type="checkbox"
              value={1}
              className="btn-green btn-sm"
              checked={this.state.showFilters}
              onChange={e => {
                this.setState({
                  showFilters: e.currentTarget.checked,
                  displayFilter: e.currentTarget.checked ? "initial" : "none"
                });
              }}
            >
              <FontAwesomeIcon icon={faFilter} />
            </ToggleButton>
          </ButtonGroup>
        ),
        headerClasses: "actionHeader",
        isDummyField: true,
        csvExport: false,
        formatExtraData: actionCellData,
        formatter: (cell, row, idx, props) => (
          <ExamInstanceActionCell
            onStart={() => this.onStart(row)}
            onStop={() => this.onStop(row)}
            onExamFileDownloadReset={() => this.onExamFileDownloadReset(row)}
            onDownloadBackup={() => this.onDownloadBackup(row)}
            onDownloadSubmission={() => this.onDownloadSubmission(row)}
            onDownloadPaperNotes={() => this.onDownloadPaperNotes(row)}
            onStudentExamFileUpload={() => this.onStudentExamFileUpload(row)}
            onDeviceAssignment={() => this.onDeviceAssignment(row)}
            onHasNotes={() => this.onHasNotes(row)}
            onGenerateOneTimeToken={() => this.onGenerateOneTimeToken(row)}
            onGenerateOneTimeStudentPassword={() => this.onGenerateOneTimeStudentLogin(row)}
            onSEBSessionQuit={() => this.onSEBSessionQuit(row)}
            onDeviceReset={() => this.onDeviceReset(row)}
            examinationStatus={props.examinationStatus}
            examination={this.props.examination}
            instanceTemplate={this.props.examination.instanceTemplate}
            instance={row}
          />
        )
      }
    ];

    const invalidDeviceInstances = this.state.instances.filter(i => i.memberDevice !== null && i.memberDevice.status === 40).length;
    const invalidDevices = (
        this.props.examination.securityLevel === 3 || this.props.examination.securityLevel === 4
    ) && invalidDeviceInstances > 0 ? (
        <Badge className="btn-red ml-3">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {invalidDeviceInstances} / { this.state.instances.length}
        </Badge>
    ) : "";

    //TODO: Setup filterable/sortable table, instance actions (handle them here), needed confirm dialogs
    return (
      <div>
        <ConfirmationDialog
          title={this.state.confirmationDialogTitle}
          body={this.state.confirmationDialogBody}
          show={this.state.showConfirmationDialog}
          dialogApiError={this.state.dialogApiError}
          onHide={() =>
            this.setState({
              dialogApiError: "",
              showConfirmationDialog: false
            })
          }
          onSubmit={this.state.onConfirmationDialogSubmit}
          confirmButtonVariant={
            this.state.confirmationDialogRightButtonVariant
          }
          confirmButtonText={this.state.confirmationDialogRightButtonText}
          cancelButtonVariant={this.state.confirmationDialogLeftButtonVariant}
        />
        <RunningDownloadDialog
          show={this.state.runningDownloadDialogShow}
          onHide={() => this.setState({ runningDownloadDialogShow: false })}
          title={this.state.runningDownloadDialogTitle}
          progress={this.state.runningDownloadDialogProgress}
        />
        <ShowSecretsDialog
            show={this.state.secretsDialogShow}
            onHide={() => this.setState({ secretsDialogShow: false, currentSecret: "" })}
            title={this.state.secretDialogTitle}
            secret={this.state.currentSecret}
            secretLabel={this.state.secretDialogLabel}
        />
        <ManagePaperNotes
          show={this.state.showPaperNotesModal}
          hasPaperNotes={this.state.instancePaperNotes.size > 0}
          paperNotes={this.state.instancePaperNotes}
          onPaperNotesUpdate={this.onPaperNotesUpdate}
          examination={this.props.examination}
          onHide={() =>
            this.setState({
              dialogApiError: "",
              showPaperNotesModal: false
            })
          }
        />
        <ManageReplacementDevices
            show={this.state.showReplacementDeviceModal}
            instance={this.state.selectedInstance}
            onHide={() =>
                this.setState({
                  dialogApiError: "",
                  showReplacementDeviceModal: false
                })
            }
        />
        <StudentExaminationFileManager
            show={this.state.studentExaminationFileManagerModal}
            instance={this.state.selectedInstance}
            instanceTemplate={this.props.examination.instanceTemplate}
            onHide={() =>
                this.setState({
                  dialogApiError: "",
                  studentExaminationFileManagerModal: false
                })
            }
        />
        <Container className="pl-0 pr-0">
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Row className="mt-3">
            <Col>
              <h4>{t("SingleExamination.InstancesTabTitle")}</h4>
            </Col>
            <Col>
              <div style={{ float: "right" }}>
                <DropdownButton
                  disabled={
                    !this.props.examination ||
                    (this.props.examination.status !== 21 &&
                      this.props.examination.status !== 60 &&
                      this.props.examination.status !== 61)
                  }
                  onClick={event => event.stopPropagation()}
                  id="dropdown-basic-button"
                  title={<FontAwesomeIcon icon={faCogs} />}
                  className="btn-green"
                >
                  <Dropdown.Item
                    onClick={() => this.setState({ showPaperNotesModal: true })}
                  >
                    {t("SingleExamination.InstanceTab.PaperNotesDialog.Button")}
                  </Dropdown.Item>
                </DropdownButton>
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="mt-2">
              <h6>
                <Badge className="btn-black">
                  <FontAwesomeIcon icon={faDownload} className="color-green" /> {
                  // eslint-disable-next-line
                  this.state.instances.filter(i => i.hasStarted).reduce((accu) => accu + 1, 0)
                  } / {
                    this.state.instances.length
                  }
                </Badge>
                {invalidDevices}
              </h6>
            </Col>
          </Row>
          <Row className="mt-1 pb-3">
            <Col>
              <PaginationTable
                data={this.state.instances}
                sizePerPage={10}
                paginationSize={3}
                columns={columns}
                keyField="_id"
                id={currentTableId}
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default withTranslation()(InstancesTab);
